// packages/main/app/web/components-v2/UnifiedImageDialog/UploadTab.tsx
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useImageAttachmentMutation } from '../../../queries/Attachments/useImageAttachmentMutation';
import { CloudUploadIcon } from '../../components/svgs/icons/CloudUploadIcon';
import { Button } from '../Button';
import { AiButton } from '../AiButton';
import { s } from './styles';

/**
 * Props for the UploadTab component
 *
 * @property onImageSelected - Callback invoked when user confirms image selection
 * @property onEditWithAI - Callback invoked when user chooses to edit image with AI
 * @property impersonationContext - Optional impersonation context for API calls
 * @property acceptedFormats - Optional array of accepted MIME types (e.g., ['image/png', 'image/gif']).
 *                             Defaults to ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif']
 */
interface UploadTabProps {
	onImageSelected: (image: Api.IFileAttachment) => void;
	onEditWithAI: (image: Api.IFileAttachment) => void;
	impersonationContext?: Api.IImpersonationContext;
	acceptedFormats?: string[];
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const DEFAULT_ACCEPTED_FORMATS = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];

/**
 * UploadTab component provides image upload functionality with drag-and-drop support,
 * file validation, and preview capabilities. Supports configurable image formats.
 *
 * Features:
 * - Drag-and-drop file upload
 * - File type and size validation
 * - Image preview with edit and confirm actions
 * - AI-powered image editing integration
 * - Configurable accepted file formats
 *
 * @example
 * ```tsx
 * <UploadTab
 *   onImageSelected={handleImageSelected}
 *   onEditWithAI={handleEditWithAI}
 *   acceptedFormats={['image/png', 'image/jpeg', 'image/gif']}
 * />
 * ```
 */
export function UploadTab({ onImageSelected, onEditWithAI, impersonationContext, acceptedFormats }: UploadTabProps) {
	const [dragActive, setDragActive] = React.useState(false);
	const [error, setError] = React.useState<string | null>(null);
	const [uploadedImage, setUploadedImage] = React.useState<Api.IFileAttachment | null>(null);
	const fileInputRef = React.useRef<HTMLInputElement>(null);

	const formats = acceptedFormats || DEFAULT_ACCEPTED_FORMATS;

	const imageAttachmentMutation = useImageAttachmentMutation({
		onSuccess: (data: Api.IFileAttachment[]) => {
			if (data?.[0]) {
				toast.success('Image uploaded successfully');
				setUploadedImage(data[0]);
			}
		},
		onError: () => {
			setError('Failed to upload image. Please try again.');
			toast.error('Failed to upload image');
		},
	});

	const validateFile = (file: File): string | null => {
		if (!formats.includes(file.type)) {
			// Transform MIME types (e.g., 'image/png', 'image/gif') to user-friendly format names (e.g., 'PNG, GIF')
			const formatNames = formats.map(f => f.replace('image/', '').toUpperCase()).join(', ');
			return `Invalid file format. Please upload ${formatNames} images.`;
		}
		if (file.size > MAX_FILE_SIZE) {
			return 'File is too large. Maximum size is 5MB.';
		}
		return null;
	};

	const handleFile = (file: File) => {
		setError(null);
		const validationError = validateFile(file);
		if (validationError) {
			setError(validationError);
			return;
		}
		imageAttachmentMutation.mutate({ files: [file], impersonationContext });
	};

	const handleDrag = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.type === 'dragenter' || e.type === 'dragover') {
			setDragActive(true);
		} else if (e.type === 'dragleave') {
			setDragActive(false);
		}
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);

		if (e.dataTransfer.files && e.dataTransfer.files[0]) {
			handleFile(e.dataTransfer.files[0]);
		}
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files[0]) {
			handleFile(e.target.files[0]);
		}
	};

	const handleClick = (e?: React.MouseEvent) => {
		e?.stopPropagation();
		fileInputRef.current?.click();
	};

	const handleAddImage = () => {
		if (uploadedImage) {
			onImageSelected(uploadedImage);
		}
	};

	const handleEditWithAI = () => {
		if (uploadedImage) {
			onEditWithAI(uploadedImage);
		}
	};

	const handleChooseAnother = () => {
		setUploadedImage(null);
		setError(null);
	};

	// Show preview if image is uploaded
	if (uploadedImage) {
		// GIF files cannot be edited with AI
		const isGif = uploadedImage.mimeType === 'image/gif';

		return (
			<div className={css(s.uploadContainer)}>
				<div className={css(s.previewSection)}>
					<div className={css(s.previewImageContainer)}>
						<img src={uploadedImage.url} alt='Uploaded image' className={css(s.previewImage)} />
					</div>
					<div className={css(s.previewActions)}>
						<Button variant='secondary' onClick={handleChooseAnother}>
							Choose Another
						</Button>
						{!isGif && <AiButton title='Edit with AI' onClick={handleEditWithAI} />}
						<Button variant='primary' onClick={handleAddImage}>
							Add Image
						</Button>
					</div>
				</div>
			</div>
		);
	}

	// Show dropzone if no image uploaded yet
	return (
		<div className={css(s.uploadContainer)}>
			<div
				className={css(s.dropzone)}
				onDragEnter={handleDrag}
				onDragLeave={handleDrag}
				onDragOver={handleDrag}
				onDrop={handleDrop}
				onClick={handleClick}
				data-drag-active={dragActive || undefined}
			>
				<div className={css(s.dropzoneContent)}>
					<CloudUploadIcon className={css(s.dropzoneIcon)} />
					<h3 className={css(s.dropzoneTitle)}>
						{imageAttachmentMutation.isPending ? 'Uploading...' : 'Drop image here or click to browse'}
					</h3>
					<p className={css(s.dropzoneDescription)}>
						{/* Transform MIME types to user-friendly format names for display */}
						Supports {formats.map(f => f.replace('image/', '').toUpperCase()).join(', ')} up to 5MB
					</p>
					{!imageAttachmentMutation.isPending && (
						<Button variant='primary' onClick={handleClick} type='button'>
							Choose File
						</Button>
					)}
				</div>
				<input
					ref={fileInputRef}
					type='file'
					accept={formats.join(',')}
					onChange={handleChange}
					className={css(s.fileInput)}
				/>
			</div>
			{error && <div className={css(s.errorMessage)}>{error}</div>}
		</div>
	);
}
